import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Card,
  CardContent,
  Tooltip,
  useTheme,
  useMediaQuery,
  Grid,
  Chip,
  Skeleton,
  Switch,
  FormControlLabel
} from "@mui/material";
import {
  Save as SaveIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Dashboard as DashboardIcon,
  MailOutline
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const alpha = (n) => String.fromCharCode(65 + n);
const cellId = (r, c) => `${alpha(c)}${r + 1}`;

const generateInitialTable = (rows, cols) => {
  const data = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      row.push({
        id: cellId(r, c),
        value: "",
        isHeader: r === 0
      });
    }
    data.push(row);
  }
  return data;
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const NonBlockingOverlay = ({ children }) => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1200,
      pointerEvents: "none",
    }}
  >
    <Box sx={{ pointerEvents: "auto" }}>
      {children}
    </Box>
  </Box>
);

const FloatingAccessMessage = ({ onClose, t, navigate }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <Paper
        elevation={3}
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 1300,
          maxWidth: 400,
          p: 3,
          backgroundColor: "background.paper",
          borderLeft: "4px solid",
          borderColor: "primary.main",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <MailOutline sx={{ color: "primary.main", fontSize: 28, mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {t("data_estructure.verify_email_title")}
              </Typography>
              <IconButton size="small" onClick={handleClose} sx={{ mt: -1, mr: -1 }}>
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("data_estructure.verify_email_new")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate("/panel/profile")}
                startIcon={<MailOutline />}
              >
                {t("data_estructure.verify_email")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
              >
                {t("data_estructure.close")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

const EditableTable = ({
  title,
  templateKey,
  onTableUpdate,
  disabledCells,
  setDisabledCells,
  enableColumnsABC,
  isBlurred = false
}) => {
  const MIN_ROWS = 6;
  const MIN_COLS = 5;
  const MAX_ROWS = 40;
  const MAX_COLS = 26;
  const PRIMARY_COLOR = '#1a237e';
  const { t } = useTranslation();

  const [table, setTable] = useState(() => generateInitialTable(MIN_ROWS, MIN_COLS));
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [cellStyles, setCellStyles] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isBlurred) {
      setFields([]);
      return;
    }

    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/demo-fields`, { withCredentials: true });
        setFields(res.data);
      } catch (err) {
        console.error("Error obteniendo campos demo:", err);
      }
    };
    fetchData();
  }, [isBlurred]);

  useEffect(() => {
    if (isBlurred) {
      const initialTable = generateInitialTable(MIN_ROWS, MIN_COLS);
      setTable(initialTable);
      if (onTableUpdate) onTableUpdate(initialTable);
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data: sheet } = await axios.get(`${API_URL}/api/tables/${templateKey}`, { withCredentials: true });

        const rows = sheet.rows || MIN_ROWS;
        const cols = sheet.cols || MIN_COLS;
        const grid = generateInitialTable(rows, cols);
        const map = new Map(sheet.cells.map((c) => [c.id, c.value]));

        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const id = cellId(r, c);
            grid[r][c].value = map.get(id) || "";
          }
        }

        if (mounted) {
          setTable(grid);
          if (onTableUpdate) onTableUpdate(grid);
        }
      } catch (err) {
        console.warn("ℹ️ No se encontraron datos guardados para", templateKey, err);
        const initialTable = generateInitialTable(MIN_ROWS, MIN_COLS);
        setTable(initialTable);
        if (onTableUpdate) onTableUpdate(initialTable);
      } finally {
        setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [templateKey, isBlurred]);

  useEffect(() => {
    if (isBlurred) return;

    if (templateKey === "partes" && fields.length > 0 && table.length > 0) {
      const updatedTable = [...table.map(row => [...row])];

      const materialesMap = {
        "bentonita": 1, "ecoperf_max_l": 2, "ecoperf_max_s": 3, "eco_ph": 4,
        "ecovis": 5, "ecotrol": 6, "cemento": 7, "cal": 8, "soda_caustica": 9
      };

      Object.values(materialesMap).forEach(rowIndex => {
        if (rowIndex < updatedTable.length && updatedTable[rowIndex].length > 2) {
          updatedTable[rowIndex][2].value = "";
        }
      });

      fields.forEach(fieldData => {
        Object.entries(fieldData).forEach(([material, valor]) => {
          if (valor !== null && valor !== "" && !isNaN(valor)) {
            const rowIndex = materialesMap[material.toLowerCase()];
            if (rowIndex && rowIndex < updatedTable.length && updatedTable[rowIndex].length > 2) {
              const currentVal = updatedTable[rowIndex][2].value || "0";
              const numValor = typeof valor === 'string' ? parseInt(valor) : valor;
              const numCurrent = parseInt(currentVal) || 0;
              updatedTable[rowIndex][2].value = (numCurrent + numValor).toString();
            }
          }
        });
      });

      for (let rowIndex = 1; rowIndex <= 8; rowIndex++) {
        if (rowIndex < updatedTable.length && updatedTable[rowIndex].length > 4) {
          const cantidad = parseInt(updatedTable[rowIndex][2].value) || 0;
          const precio = parseInt(updatedTable[rowIndex][3].value) || 0;
          updatedTable[rowIndex][4].value = (cantidad * precio).toString();
        }
      }

      setTable(updatedTable);
      if (onTableUpdate) onTableUpdate(updatedTable);
    }
  }, [fields, templateKey, isBlurred]);

  useEffect(() => {
    if (isBlurred) return;

    if (templateKey === "desarrollo" && fields.length > 0) {
      const consumos = fields;
      const updatedTable = [...table.map(row => [...row])];

      const extraerRango = (descripcion) => {
        const match = descripcion?.match(/(\d+)-(\d+)/);
        return match ? { inicio: parseInt(match[1], 10), fin: parseInt(match[2], 10) } : null;
      };

      const rangosTabla = updatedTable.slice(1).map((row, i) => {
        const rango = extraerRango(row[0]?.value);
        if (!rango) return null;
        return {
          inicio: rango.inicio,
          fin: rango.fin,
          precio: parseFloat(row[2]?.value) || 0,
          filaIndex: i + 1
        };
      }).filter(Boolean);

      updatedTable.slice(1).forEach(row => {
        if (row[3]) row[3].value = 0;
        if (row[4]) row[4].value = 0;
      });

      consumos.forEach((consumo) => {
        const inicioC = parseInt(consumo.prof_inicial, 10);
        const finC = parseInt(consumo.prof_final, 10);
        const metrosConsumo = parseInt(consumo.metros_perf, 10);
        const longitudConsumo = finC - inicioC + 1;

        let metrosAsignados = 0;
        const rangosSolape = rangosTabla.filter(r => finC >= r.inicio && inicioC <= r.fin);

        rangosSolape.forEach((rango, rIdx) => {
          const solapeInicio = Math.max(inicioC, rango.inicio);
          const solapeFin = Math.min(finC, rango.fin);
          const solape = solapeFin - solapeInicio + 1;

          if (solape > 0) {
            let metrosTabla = rIdx === rangosSolape.length - 1
              ? metrosConsumo - metrosAsignados
              : Math.floor((solape / longitudConsumo) * metrosConsumo);

            metrosAsignados += metrosTabla;
            const filaIndex = rango.filaIndex;

            if (updatedTable[filaIndex][3]) updatedTable[filaIndex][3].value += metrosTabla;
            if (updatedTable[filaIndex][4]) updatedTable[filaIndex][4].value += metrosTabla * rango.precio;
          }
        });
      });

      updatedTable.slice(1).forEach(row => {
        if (row[3]?.value === 0) {
          row[3].value = "";
          row[4].value = "";
        }
      });

      setTable(updatedTable);
      if (onTableUpdate) onTableUpdate(updatedTable);
    }
  }, [fields, templateKey, isBlurred]);

  useEffect(() => {
    if (isBlurred) return;

    if (templateKey === "desarrollo" && onTableUpdate) {
      onTableUpdate(table);
    }
  }, [table, templateKey, onTableUpdate, isBlurred]);

  useEffect(() => {
    if (isBlurred) return;

    const styleCells = ["E2", "E3", "E4", "E5", "E6", "E7", "E8", "E9"];
    setCellStyles(prev => {
      const newStyles = { ...prev };
      styleCells.forEach(id => {
        newStyles[id] = { color: "green", fontWeight: "bold" };
      });
      return newStyles;
    });
  }, [templateKey, isBlurred]);

  useEffect(() => {
    const newDisabledCells = new Set();

    for (let r = 0; r < table.length; r++) {
      for (let c = 0; c < table[0].length; c++) {
        const cellIdToDisable = `${cellId(r, c)}_${templateKey}`;
        newDisabledCells.add(cellIdToDisable);
      }
    }

    if (enableColumnsABC && !isBlurred) {
      for (let r = 1; r < table.length; r++) {
        for (let c = 0; c < 3; c++) {
          const cellIdToEnable = `${cellId(r, c)}_${templateKey}`;
          newDisabledCells.delete(cellIdToEnable);
        }
      }
    }

    setDisabledCells(prev => {
      const mergedSet = new Set([...prev]);
      Array.from(mergedSet).forEach(cellId => {
        if (cellId.endsWith(`_${templateKey}`)) {
          mergedSet.delete(cellId);
        }
      });
      newDisabledCells.forEach(cellId => mergedSet.add(cellId));
      return mergedSet;
    });
  }, [enableColumnsABC, templateKey, table, setDisabledCells, isBlurred]);

  const handleChange = (rowIdx, colIdx, newValue) => {
    if (isBlurred) return;

    const newTable = [...table.map(row => [...row])];
    newTable[rowIdx][colIdx] = { ...newTable[rowIdx][colIdx], value: newValue };

    if ((colIdx === 2 || colIdx === 3) && rowIdx > 0 && rowIdx < newTable.length && newTable[rowIdx].length > 4) {
      const cantidad = parseInt(newTable[rowIdx][2].value) || 0;
      const precio = parseInt(newTable[rowIdx][3].value) || 0;
      newTable[rowIdx][4].value = (cantidad * precio).toString();
    }

    setTable(newTable);
    if (onTableUpdate) onTableUpdate(newTable);
  };

  const addRow = () => {
    if (isBlurred) {
      alert("⚠️ Verifica tu email para modificar tablas.");
      return;
    }

    if (table.length >= MAX_ROWS) {
      setSnackbar({ open: true, message: t("data_estructure.max_rows"), severity: "warning" });
      return;
    }
    const newRow = Array.from({ length: table[0].length }, (_, c) => ({
      id: cellId(table.length, c),
      value: "",
      isHeader: false
    }));
    const newTable = [...table, newRow];
    setTable(newTable);
    if (onTableUpdate) onTableUpdate(newTable);
  };

  const removeRow = () => {
    if (isBlurred) {
      alert("⚠️ Verifica tu email para modificar tablas.");
      return;
    }

    if (table.length <= MIN_ROWS + 1) {
      setSnackbar({ open: true, message: t("data_estructure.min_rows"), severity: "warning" });
      return;
    }
    const newTable = table.slice(0, -1);
    setTable(newTable);
    if (onTableUpdate) onTableUpdate(newTable);
  };

  const addColumn = () => {
    if (isBlurred) {
      alert("⚠️ Verifica tu email para modificar tablas.");
      return;
    }

    if (table[0].length >= MAX_COLS) {
      setSnackbar({ open: true, message: t("data_estructure.max_cols"), severity: "warning" });
      return;
    }
    const newTable = table.map((row, r) => [
      ...row,
      { id: cellId(r, row.length), value: "", isHeader: r === 0 }
    ]);
    setTable(newTable);
    if (onTableUpdate) onTableUpdate(newTable);
  };

  const removeColumn = () => {
    if (isBlurred) {
      alert("⚠️ Verifica tu email para modificar tablas.");
      return;
    }

    if (table[0].length <= MIN_COLS) {
      setSnackbar({ open: true, message: t("data_estructure.min_cols"), severity: "warning" });
      return;
    }
    const newTable = table.map(row => row.slice(0, -1));
    setTable(newTable);
    if (onTableUpdate) onTableUpdate(newTable);
  };

  const saveAll = async () => {
    if (isBlurred) {
      alert("⚠️ Verifica tu email para guardar datos.");
      return;
    }

    try {
      setSaving(true);
      const flatCells = table.flat().map((c) => ({ id: c.id, value: c.value }));
      await axios.post(`${API_URL}/api/tables`, {
        templateKey, title, rows: table.length, cols: table[0].length, cells: flatCells,
      }, { withCredentials: true });
      setSnackbar({ open: true, message: t("data_estructure.save_success"), severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: t("data_estructure.save_error", { error: err.message }), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getCellStyle = (cellId) => {
    return cellStyles[cellId] || {};
  };

  const getTextFieldSx = (cell) => {
    const cellStyle = getCellStyle(cell.id);
    return {
      height: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.875rem',
        height: '100%',
        padding: 0,
        '&:hover fieldset': { borderColor: PRIMARY_COLOR },
        backgroundColor: cellStyle.backgroundColor || undefined,
        '&.Mui-disabled': {
          backgroundColor: 'rgba(200,200,200,0.3)',
        },
      },
      '& .MuiOutlinedInput-input': {
        display: 'flex',
        justifyContent: cellStyle.textAlign || 'center',
        alignItems: 'center',
        height: '100%',
        textAlign: cellStyle.textAlign || 'center',
        color: cellStyle.color || undefined,
        fontWeight: cellStyle.fontWeight || undefined,
        ...(cell.isHeader && {
          fontWeight: 'bold',
          textTransform: 'uppercase'
        }),
      },
      '& .MuiOutlinedInput-input.Mui-disabled': {
        WebkitTextFillColor: cellStyle.color || undefined,
        color: cellStyle.color || undefined,
        fontWeight: cellStyle.fontWeight || undefined,
        textAlign: cellStyle.textAlign || 'center',
      },
    };
  };

  return (
    <motion.div
      whileHover={!isBlurred ? { scale: 1.005 } : {}}
      transition={{ duration: 0.2 }}
    >
      <Card sx={{
        boxShadow: 3,
        borderRadius: 2,
        overflow: 'hidden',
        mb: 4,
        filter: isBlurred ? "blur(2px)" : "none",
        opacity: isBlurred ? 0.7 : 1,
        transition: "filter 0.3s ease, opacity 0.3s ease",
        position: "relative"
      }}>
        <Box sx={{ p: 2, bgcolor: PRIMARY_COLOR, color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t(title)}</Typography>
        </Box>

        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer sx={{
                maxHeight: 440,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                opacity: isBlurred ? 0.8 : 1
              }}>
                <Table stickyHeader size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{
                        width: 60,
                        fontWeight: 'bold',
                        bgcolor: '#f5f5f5',
                        borderRight: '1px solid #e0e0e0'
                      }}>
                        {t("data_estructure.id")}
                      </TableCell>
                      {table[0].map((_, c) => (
                        <TableCell key={c} sx={{
                          fontWeight: 'bold',
                          bgcolor: '#f5f5f5',
                          textAlign: 'center',
                          borderRight: c < table[0].length - 1 ? '1px solid #e0e0e0' : 'none'
                        }}>
                          {alpha(c)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.map((row, r) => (
                      <TableRow key={r} hover>
                        <TableCell sx={{
                          fontWeight: 'bold',
                          bgcolor: '#fafafa',
                          borderRight: '1px solid #e0e0e0'
                        }}>
                          {r + 1}
                        </TableCell>
                        {row.map((cell, c) => (
                          <TableCell key={cell.id} sx={{
                            p: 0.5,
                            borderRight: c < row.length - 1 ? '1px solid #e0e0e0' : 'none',
                            ...(cell.isHeader && {
                              fontWeight: 'bold',
                              bgcolor: '#e3f2fd',
                              textTransform: 'uppercase'
                            })
                          }}>
                            <TextField
                              value={cell.value}
                              onChange={(e) => handleChange(r, c, e.target.value)}
                              placeholder={t(cell.id)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              disabled={disabledCells.has(`${cell.id}_${templateKey}`) || isBlurred}
                              sx={getTextFieldSx(cell)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{
                mt: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: 'center',
                opacity: isBlurred ? 0.8 : 1
              }}>
                {[
                  { action: addRow, disabled: table.length >= MAX_ROWS || isBlurred, text: t("data_estructure.row"), icon: <AddIcon /> },
                  { action: removeRow, disabled: table.length <= MIN_ROWS + 1 || isBlurred, text: t("data_estructure.row"), icon: <RemoveIcon /> },
                  { action: addColumn, disabled: table[0].length >= MAX_COLS || isBlurred, text: t("data_estructure.col"), icon: <AddIcon /> },
                  { action: removeColumn, disabled: table[0].length <= MIN_COLS || isBlurred, text: t("data_estructure.col"), icon: <RemoveIcon /> },
                ].map(({ action, disabled, text, icon }, index) => (
                  <Tooltip key={index} title={isBlurred ? "Verifica tu email para modificar" : t(`data_estructure.${action.name}`)}>
                    <span>
                      <Button
                        variant="outlined"
                        onClick={action}
                        disabled={disabled}
                        startIcon={icon}
                        size="small"
                      >
                        {text}
                      </Button>
                    </span>
                  </Tooltip>
                ))}

                <Box sx={{ flexGrow: 1 }} />

                <Tooltip title={isBlurred ? "Verifica tu email para guardar" : ""}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={saving || isBlurred}
                      onClick={saveAll}
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      sx={{ minWidth: 120 }}
                    >
                      {saving ? t("data_estructure.saving") : t("data_estructure.save")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {!isBlurred && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </motion.div>
  );
};


const CostPieChart = ({ data, isBlurred = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);

  const COLORS = useMemo(() => [
    theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main,
    theme.palette.warning.main, theme.palette.info.main, theme.palette.error.main,
    '#6A0DAD', '#FF6B35'
  ], [theme]);

  const { total, sortedData } = useMemo(() => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    return { total, sortedData };
  }, [data]);



  const CustomTooltip = useCallback(({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      return (
        <Card elevation={4} sx={{ p: 1.5, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
            {t(`data_estructure.${payload[0].name}`) || payload[0].name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{t('data_estructure.value')}:</Typography>
            <Typography variant="body2" fontWeight="bold">${payload[0].value.toLocaleString('es-ES')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t('data_estructure.percentage')}:</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">{percentage}%</Typography>
          </Box>
        </Card>
      );
    }
    return null;
  }, [total, theme, t]);
  if (isBlurred) {
    return (
      <Card sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        filter: "blur(2px)",
        opacity: 0.7,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card sx={{
      p: 3,
      height: '100%',
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 2 }}>
        {t('data_estructure.costDistribution')}
      </Typography>

      <Box sx={{ position: 'relative', height: 220, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={1}
              dataKey="value"
              activeIndex={activeIndex}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke={theme.palette.common.white} strokeWidth={2}
                  opacity={activeIndex === index ? 1 : 0.8} style={{ transition: 'opacity 0.2s ease', cursor: 'pointer' }} />
              ))}
            </Pie>
            <RechartsTooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1300 }} />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {t('data_estructure.total')}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary">
            ${total.toLocaleString('es-ES')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 3, p: 2.5, bgcolor: 'primary.main', borderRadius: 2, textAlign: 'center', color: 'primary.contrastText' }}>
        <Typography variant="subtitle2" fontWeight="500" gutterBottom>{t('data_estructure.totalInvestment')}</Typography>
        <Typography variant="h4" fontWeight="700">${total.toLocaleString('es-ES')}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {t('data_estructure.registeredMaterials', { count: data.length })}
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
          {t('data_estructure.detailByMaterial')}
        </Typography>

        <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1 }}>
          {sortedData.map((entry, index) => {
            const percentage = ((entry.value / total) * 100).toFixed(1);
            const originalIndex = data.findIndex(item => item.name === entry.name);

            return (
              <Card key={index} elevation={0} sx={{
                mb: 1.5, p: 1.5, bgcolor: activeIndex === originalIndex ? 'action.hover' : 'grey.50',
                border: `1px solid ${theme.palette.divider}`, borderRadius: 2, transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'action.hover', borderColor: theme.palette.primary.light }
              }}
                onMouseEnter={() => setActiveIndex(originalIndex)} onMouseLeave={() => setActiveIndex(null)}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: '3px', bgcolor: COLORS[originalIndex % COLORS.length], mr: 2, flexShrink: 0,
                      border: `2px solid ${theme.palette.common.white}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} />
                    <Typography variant="body2" fontWeight={activeIndex === originalIndex ? 600 : 500}
                      sx={{ color: activeIndex === originalIndex ? 'primary.main' : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t(`data_estructure.${entry.name}`) || entry.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 2 }}>
                    <Typography variant="body2" fontWeight="700" color="text.primary" sx={{ minWidth: 80, textAlign: 'right' }}>
                      ${entry.value.toLocaleString('es-ES')}
                    </Typography>
                    <Chip label={`${percentage}%`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, minWidth: 50 }} />
                  </Box>
                </Box>
                <Box sx={{ mt: 1, width: '100%', height: 4, bgcolor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', bgcolor: COLORS[originalIndex % COLORS.length], width: `${percentage}%`, borderRadius: 2, transition: 'width 0.3s ease' }} />
                </Box>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

const MaterialsBarChart = ({ data, isBlurred = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [hoverIndex, setHoverIndex] = useState(null);



  const { sortedData, nonZeroData, total, mostExpensive, leastExpensive } = useMemo(() => {
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    const nonZeroData = sortedData.filter(item => item.value > 0);
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const mostExpensive = nonZeroData[0];
    const leastExpensive = nonZeroData.length > 1 ? nonZeroData[nonZeroData.length - 1] : null;
    return { sortedData, nonZeroData, total, mostExpensive, leastExpensive };
  }, [data]);

  const CustomTooltip = useCallback(({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      const nameKey = `data_estructure.${payload[0].payload.name}`;
      return (
        <Card elevation={4} sx={{ p: 2, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 180 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
            {t(nameKey) || payload[0].payload.name}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('data_estructure.value')}:</Typography>
            <Typography variant="body1" fontWeight="bold">${payload[0].value.toLocaleString('es-ES')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">{t('data_estructure.percentage')}:</Typography>
            <Chip label={`${percentage}%`} size="small" color="primary" variant="outlined" />
          </Box>
        </Card>
      );
    }
    return null;
  }, [total, theme, t]);

  const getBarColor = useCallback((index, isHovered) => {
    const baseColor = theme.palette.primary.main;
    if (isHovered) return theme.palette.primary.dark;
    const intensity = 0.7 + (index * 0.03);
    return intensity > 1 ? theme.palette.primary.dark : baseColor;
  }, [theme]);

  if (isBlurred) {
    return (
      <Card sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        filter: "blur(2px)",
        opacity: 0.7,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }
  if (!data || data.length === 0) {
    return (
      <Card sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card sx={{
      p: 3,
      height: '100%',
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" gutterBottom align="center" sx={{
        fontWeight: 600,
        color: theme.palette.primary.main,
        mb: 3,
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {t('data_estructure.titleBar')}
      </Typography>

      <Box sx={{ flexGrow: 1, minHeight: 320, mb: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={sortedData}
            layout="vertical"
            margin={{ top: 10, bottom: 10 }}
            barSize={32}
            onMouseMove={(e) => { if (e.activeTooltipIndex !== undefined) setHoverIndex(e.activeTooltipIndex); }}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" horizontal vertical={false} />
            <XAxis
              type="number"
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              tickFormatter={(value) => `$${value.toLocaleString('es-ES')}`}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              axisLine={false}
              tickLine={false}
              tick={({ x, y, payload }) => (
                <text x={x} y={y} fill={theme.palette.text.primary} fontSize={12} fontWeight={500}
                  textAnchor="end" dominantBaseline="middle">
                  {t(`data_estructure.${payload.value}`) || payload.value}
                </text>
              )}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name={t('data_estructure.total_investment')} radius={[0, 4, 4, 0]}>
              {sortedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(index, hoverIndex === index)} stroke={theme.palette.common.white}
                  strokeWidth={1} opacity={hoverIndex === null ? 0.85 : hoverIndex === index ? 1 : 0.6}
                  style={{ transition: 'all 0.2s ease', cursor: 'pointer' }} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 'auto', pb: 1 }}>
        <Typography variant="subtitle2" fontWeight="600" gutterBottom
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mb: 2.5,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem'
          }}>
          {t('data_estructure.summary')}
        </Typography>

        <Grid container spacing={2} justifyContent="center">
          {[
            {
              value: total,
              label: t('data_estructure.total_investment'),
              sublabel: t('data_estructure.general_total'),
              color: 'primary',
              content: `$${total.toLocaleString('es-ES')}`
            },
            {
              value: mostExpensive?.value,
              label: t('data_estructure.most_expensive'),
              name: mostExpensive?.name,
              color: 'success',
              content: `$${mostExpensive?.value.toLocaleString('es-ES') || '0'}`
            },
            {
              value: leastExpensive?.value,
              label: t('data_estructure.least_expensive'),
              name: leastExpensive?.name,
              color: 'info',
              content: `$${leastExpensive?.value.toLocaleString('es-ES') || '0'}`
            },
          ].map((item, index) => (
            <Grid item xs={12} sm={4} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card elevation={2} sx={{
                p: 2,
                width: '100%',
                maxWidth: 200,
                bgcolor: `${item.color}.main`,
                borderRadius: 2,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 100
              }}>
                <Typography variant="caption" color={`${item.color}.contrastText`} display="block" gutterBottom sx={{ fontWeight: 500 }}>
                  {item.label}
                </Typography>
                {item.name ? (
                  <>
                    <Typography variant="body2" fontWeight="600" color={`${item.color}.contrastText`} noWrap sx={{ mb: 0.5 }}>
                      {t(`data_estructure.${item.name}`) || item.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" fontWeight="700" color={`${item.color}.contrastText`}>
                      {item.content}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" fontWeight="700" color={`${item.color}.contrastText`} sx={{ mb: 0.5 }}>
                      {item.content}
                    </Typography>
                    <Typography variant="caption" color={`${item.color}.contrastText`} sx={{ opacity: 0.9 }}>
                      {item.sublabel}
                    </Typography>
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Card>
  );
};

const DesarrolloBarChart = ({ data, loading = false, isBlurred = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [hoverIndex, setHoverIndex] = useState(null);



  const processDesarrolloData = useCallback(() => {
    if (!data || data.length === 0) return [];
    const processedData = [];

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (row && row.length >= 5 && row[0]?.value && row[4]?.value) {
        const rango = row[0].value;
        const total = parseFloat(row[4].value) || 0;
        if (total > 0) {
          const match = rango.match(/(\d+)-(\d+)/);
          processedData.push({
            name: rango.replace("Perforación", t("data_estructure.perforacion")),
            value: total,
            metros: parseFloat(row[3]?.value) || 0,
            precio: parseFloat(row[2]?.value) || 0,
            startNumber: match ? parseInt(match[1], 10) : 0
          });
        }
      }
    }

    return processedData.sort((a, b) => a.startNumber - b.startNumber);
  }, [data, t]);

  const chartData = processDesarrolloData();
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = useCallback(
    ({ active, payload }) => {
      if (active && payload && payload.length) {
        const item = payload[0].payload;
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";

        return (
          <Card
            elevation={4}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              minWidth: 200
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
              {item.name}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("data_estructure.total")}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                ${item.value.toLocaleString("es-ES")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("data_estructure.metros")}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {item.metros.toLocaleString("es-ES")} m
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t("data_estructure.precio_m")}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                ${item.precio.toLocaleString("es-ES")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                {t("data_estructure.participacion")}
              </Typography>
              <Chip label={`${percentage}%`} size="small" color="primary" variant="outlined" />
            </Box>
          </Card>
        );
      }
      return null;
    },
    [total, theme, t]
  );
  if (isBlurred) {
    return (
      <Card sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        filter: "blur(2px)",
        opacity: 0.7,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }
  if (loading) {
    return (
      <Card
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="text" width="60%" height={24} />
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Typography variant="body1" color="text.secondary">
          <CircularProgress />
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        p: 3,
        height: "100%",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        align="center"
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 3,
          textTransform: "uppercase",
          letterSpacing: "0.5px"
        }}
      >
        {t("data_estructure.costos_rango")}
      </Typography>

      <Box sx={{ flexGrow: 1, minHeight: 300, mb: 3 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, bottom: 40 }}
            barSize={30}
            onMouseMove={(e) => {
              if (e.activeTooltipIndex !== undefined) setHoverIndex(e.activeTooltipIndex);
            }}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" horizontal vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fill: theme.palette.text.primary, fontSize: 11 }}
              tickFormatter={(value) => `$${value.toLocaleString("es-ES")}`}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name={t("data_estructure.costo_total")} fill={theme.palette.primary.main} radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={theme.palette.primary.main}
                  opacity={hoverIndex === index ? 1 : 0.7}
                  style={{ transition: "all 0.2s ease", cursor: "pointer" }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: "auto", p: 2.5, bgcolor: "grey.50", borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Typography
          variant="subtitle2"
          fontWeight="600"
          gutterBottom
          sx={{ color: "text.secondary", textAlign: "center", mb: 2 }}
        >
          {t("data_estructure.resumen_desarrollo")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("data_estructure.total_invertido")}
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">
              ${total.toLocaleString("es-ES")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              {t("data_estructure.rangos_activos")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {chartData.length}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {t("data_estructure.metros_totales")}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {chartData.reduce((sum, item) => sum + item.metros, 0).toLocaleString("es-ES")} m
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

const DesarrolloPieChart = ({ data, isBlurred = false }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(null);



  const COLORS = useMemo(() => [
    theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main,
    theme.palette.warning.main, theme.palette.info.main, theme.palette.error.main,
    "#6A0DAD", "#FF6B35"
  ], [theme]);

  const processPieData = useCallback(() => {
    if (!data || data.length === 0) return [];
    const processedData = [];
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (row && row.length >= 5 && row[0]?.value && row[4]?.value) {
        const total = parseFloat(row[4].value) || 0;
        if (total > 0) {
          processedData.push({
            name: row[0].value.replace("Perforación", t("data_estructure.perforacion")),
            value: total,
            metros: parseFloat(row[3]?.value) || 0
          });
        }
      }
    }
    return processedData.sort((a, b) => b.value - a.value);
  }, [data, t]);

  const pieData = processPieData();
  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = useCallback(({ active, payload }) => {
    if (active && payload && payload.length) {
      const percentage = total > 0 ? ((payload[0].value / total) * 100).toFixed(1) : "0";
      return (
        <Card
          elevation={8}
          sx={{
            p: 1.5,
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            zIndex: 1300,
            position: 'relative'
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
            {payload[0].name}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{t("data_estructure.valor")}</Typography>
            <Typography variant="body2" fontWeight="bold">${payload[0].value.toLocaleString("es-ES")}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t("data_estructure.metros")}</Typography>
            <Typography variant="body2" fontWeight="bold">{payload[0].payload.metros.toLocaleString("es-ES")} m</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{t("data_estructure.porcentaje")}</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary">{percentage}%</Typography>
          </Box>
        </Card>
      );
    }
    return null;
  }, [total, theme, t]);
  if (isBlurred) {
    return (
      <Card sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        filter: "blur(2px)",
        opacity: 0.7,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }
  if (!pieData || pieData.length === 0) {
    return (
      <Card sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card sx={{
      p: 3,
      height: "100%",
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      position: 'relative'
    }}>
      <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 2 }}>
        {t("data_estructure.distribution")}
      </Typography>

      <Box sx={{ position: "relative", height: 250, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke={theme.palette.common.white}
                  strokeWidth={2}
                  opacity={activeIndex === index ? 1 : 0.8}
                />
              ))}
            </Pie>
            <RechartsTooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1300 }}
            />
          </PieChart>
        </ResponsiveContainer>

        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          zIndex: 1,
          pointerEvents: 'none'
        }}>
          <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
            {t("data_estructure.total")}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            ${total.toLocaleString("es-ES")}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2, flexGrow: 1, overflow: "auto" }}>
        <Grid container spacing={1}>
          {pieData.map((entry, index) => {
            const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : "0";
            return (
              <Grid item xs={6} key={index}>
                <Box sx={{ display: "flex", alignItems: "center", p: 1 }}>
                  <Box sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "2px",
                    bgcolor: COLORS[index % COLORS.length],
                    mr: 1,
                    flexShrink: 0
                  }} />
                  <Typography variant="caption" sx={{ flex: 1 }}>{entry.name}</Typography>
                  <Typography variant="caption" fontWeight="bold" sx={{ ml: 1 }}>{percentage}%</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Card>
  );
};

const CostStructure = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [partesTableData, setPartesTableData] = useState([]);
  const [desarrolloTableData, setDesarrolloTableData] = useState([]);
  const [costData, setCostData] = useState([]);
  const [materialsData, setMaterialsData] = useState([]);
  const [disabledCells, setDisabledCells] = useState(new Set());
  const [enableColumnsABC, setEnableColumnsABC] = useState(false);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [showAccessMessage, setShowAccessMessage] = useState(true);
  const navigate = useNavigate();

  const { auth } = useAuth();

  const { t } = useTranslation();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/route-access/cost-structure`, { withCredentials: true });

        const isBlocked = res.data.blocked &&
          res.data.requireVerifiedEmail &&
          !auth.isEmailVerified;

        setAccessBlocked(isBlocked);
        setShowAccessMessage(isBlocked);
      } catch (err) {
        console.error("Error verificando acceso:", err);
        setAccessBlocked(false);
        setShowAccessMessage(false);
      } finally {
        setAccessChecked(true);
      }
    };

    checkAccess();
  }, []);

  useEffect(() => {
    if (partesTableData.length > 0) {
      const materialesNombres = ["Bentonita", "Ecoperf Max L", "Ecoperf Max S", "Eco PH", "Ecovis", "Ecotrol", "Cemento", "Cal", "Soda Cáustica"];
      const nuevosCostos = [];
      const nuevosMateriales = [];

      for (let i = 1; i <= 8; i++) {
        if (partesTableData[i] && partesTableData[i].length > 4) {
          const valor = parseInt(partesTableData[i][4].value) || 0;
          if (valor > 0) {
            nuevosCostos.push({ name: materialesNombres[i - 1], value: valor });
            nuevosMateriales.push({ name: materialesNombres[i - 1], value: valor });
          }
        }
      }
      setCostData(nuevosCostos);
      setMaterialsData(nuevosMateriales);
    } else {
      setCostData([]);
      setMaterialsData([]);
    }
  }, [partesTableData]);

  const handlePartesTableUpdate = useCallback((updatedTable) => {
    setPartesTableData(updatedTable);
  }, []);

  const handleDesarrolloTableUpdate = useCallback((updatedTable) => {
    setDesarrolloTableData(updatedTable);
  }, []);

  const handleToggleColumnsABC = useCallback(() => {
    if (accessBlocked) {
      alert("⚠️ Verifica tu email para habilitar esta funcionalidad.");
      return;
    }
    setEnableColumnsABC(prev => !prev);
  }, [accessBlocked]);

  if (!accessChecked) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="sticky" sx={{ bgcolor: '#1a237e', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
          <Toolbar>
            <DashboardIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              {t("data_estructure.appTitle")}
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: isMobile ? 2 : 4, display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mensaje flotante NO BLOQUEANTE */}
      {/* {accessBlocked && showAccessMessage && (
        <FloatingAccessMessage onClose={() => setShowAccessMessage(false)} t={t} navigate={navigate} />
      )} */}

      <AppBar position="sticky" sx={{
        bgcolor: '#1a237e',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        filter: accessBlocked ? "blur(2px)" : "none",
        opacity: accessBlocked ? 0.8 : 1,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {t("data_estructure.appTitle")}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{
        p: isMobile ? 2 : 4,
        filter: accessBlocked ? "blur(2px)" : "none",
        opacity: accessBlocked ? 0.7 : 1,
        transition: "filter 0.3s ease, opacity 0.3s ease"
      }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 4 }}>
          {t("data_estructure.title")}
        </Typography>

        {/* Sección de controles */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'start',
          my: 2,
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={enableColumnsABC}
                onChange={handleToggleColumnsABC}
                color="primary"
                disabled={accessBlocked}
              />
            }
          />

          <Button
            variant="contained"
            disabled={accessBlocked}
            sx={{
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' }
            }}
          >
            {t("data_estructure.request_help")}
          </Button>
        </Box>

        <EditableTable
          title={t("data_estructure.desarrolloTrabajo")}
          templateKey="desarrollo"
          onTableUpdate={handleDesarrolloTableUpdate}
          disabledCells={disabledCells}
          setDisabledCells={setDisabledCells}
          enableColumnsABC={enableColumnsABC}
          isBlurred={accessBlocked}
        />

        <EditableTable
          title={t("data_estructure.materialesInyeccion")}
          templateKey="partes"
          onTableUpdate={handlePartesTableUpdate}
          disabledCells={disabledCells}
          setDisabledCells={setDisabledCells}
          enableColumnsABC={enableColumnsABC}
          isBlurred={accessBlocked}
        />

        <Typography variant="h5" sx={{
          color: 'primary.main',
          fontWeight: 600,
          mb: 3,
          mt: 4,
          filter: accessBlocked ? "blur(2px)" : "none",
          opacity: accessBlocked ? 0.8 : 1
        }}>
          {t("data_estructure.analisisDesarrollo")}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 4,
          filter: accessBlocked ? "blur(2px)" : "none",
          opacity: accessBlocked ? 0.7 : 1
        }}>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <DesarrolloBarChart data={desarrolloTableData} isBlurred={accessBlocked} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <DesarrolloPieChart data={desarrolloTableData} isBlurred={accessBlocked} />
          </Box>
        </Box>

        <Typography variant="h5" sx={{
          color: 'primary.main',
          fontWeight: 600,
          mb: 3,
          filter: accessBlocked ? "blur(2px)" : "none",
          opacity: accessBlocked ? 0.8 : 1
        }}>
          {t("data_estructure.analisisMateriales")}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mt: 2,
          filter: accessBlocked ? "blur(2px)" : "none",
          opacity: accessBlocked ? 0.7 : 1
        }}>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <CostPieChart data={costData} isBlurred={accessBlocked} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <MaterialsBarChart data={materialsData} isBlurred={accessBlocked} />
          </Box>
        </Box>
      </Box>

      {/* Overlay CENTRADO pero NO BLOQUEANTE */}
      {accessBlocked && (
        <NonBlockingOverlay>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={4}
              sx={{
                textAlign: "center",
                p: 4,
                backgroundColor: "background.paper",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
                border: "1px solid",
                borderColor: "divider",
                maxWidth: 500,
                mx: 2,
                pointerEvents: "auto",
              }}
            >
              <MailOutline sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />

              <Typography variant="h5" gutterBottom fontWeight="bold">
                {t("data_estructure.verify_email_title")}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t("data_estructure.verify_email_needed")}
              </Typography>

              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("data_estructure.verify_email_instruction")}
              </Typography> */}

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/panel/profile')}
                  startIcon={<MailOutline />}
                >
                  {t("data_estructure.verify_email")}
                </Button>
                {/* <Button
                  variant="outlined"
                  onClick={() => {
                    alert("Si no recibiste el email de verificación, contacta al administrador.");
                  }}
                >
                  ¿Problemas?
                </Button> */}
              </Box>
            </Paper>
          </motion.div>
        </NonBlockingOverlay>
      )}
    </Box>
  );
};

export default CostStructure;